import React, { useState, useEffect } from 'react';
import { processClusterData } from '../utils/clusterProcessing';
import clustersData from '../data/class_clusters.json';
import {
  Box,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
  Table,
  PieChart,
  Cards,
  StatusIndicator,
  Link,
  TextFilter,
  Modal,
  ProgressBar,
  Select,
  ButtonDropdown
} from '@cloudscape-design/components';

export function ClassClustersTab({ project }) {
  const [data, setData] = useState(null);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [selectedView, setSelectedView] = useState({ value: 'overview' });

  useEffect(() => {
    const processed = processClusterData(clustersData);
    setData(processed);
  }, []);

  if (!data) return null;

  // Modify the table columns based on the actual data
  const tableColumns = [
    {
      id: 'name',
      header: 'Class name',
      cell: item => (
        <Link onFollow={() => setSelectedClass(item)}>
          {item.name}
        </Link>
      ),
      sortingField: 'name'
    },
    {
      id: 'type',
      header: 'Type',
      cell: item => item.type,
      sortingField: 'type'
    },
    {
      id: 'confidence',
      header: 'Classification confidence',
      cell: item => (
        <ProgressBar
          value={item.confidence}
          label={`${item.confidence}%`}
        />
      ),
      sortingField: 'confidence'
    },
    {
      id: 'dependencies',
      header: 'Dependencies',
      cell: item => item.dependencies,
      sortingField: 'dependencies'
    },
    {
      id: 'sourceFile',
      header: 'Source file',
      cell: item => item.sourceFile,
      sortingField: 'sourceFile'
    }
  ];

  // Update the class details modal content
  const renderClassDetails = (classInfo) => (
    <SpaceBetween size="l">
      <Container header={<Header variant="h3">Classification Details</Header>}>
        <ColumnLayout columns={2}>
          <SpaceBetween size="s">
            <Box variant="awsui-key-label">Type</Box>
            <Box>{classInfo.type}</Box>
            <Box variant="awsui-key-label">Source File</Box>
            <Box>{classInfo.sourceFile}</Box>
          </SpaceBetween>
          <SpaceBetween size="s">
            <Box variant="awsui-key-label">Confidence</Box>
            <ProgressBar
              value={classInfo.confidence}
              label={`${classInfo.confidence}%`}
            />
            <Box variant="awsui-key-label">Evidence</Box>
            <Box>{classInfo.evidence.join(', ')}</Box>
          </SpaceBetween>
        </ColumnLayout>
      </Container>

      {classInfo.operations?.length > 0 && (
        <Container header={<Header variant="h3">Operations</Header>}>
          <Table
            columnDefinitions={[
              { header: "Operation", cell: item => item.name },
              { header: "Business Process", cell: item => item.business_process },
              { header: "Transaction Scope", cell: item => item.transaction_scope }
            ]}
            items={classInfo.operations}
            variant="embedded"
          />
        </Container>
      )}

      {classInfo.attributes?.length > 0 && (
        <Container header={<Header variant="h3">Attributes</Header>}>
          <Table
            columnDefinitions={[
              { header: "Name", cell: item => item.name },
              { header: "Type", cell: item => item.type }
            ]}
            items={classInfo.attributes}
            variant="embedded"
          />
        </Container>
      )}
    </SpaceBetween>
  );

  return (
    <SpaceBetween size="l">
      {/* Cluster Overview */}
      <Container
        header={
          <Header
            variant="h2"
            description="Classification of project classes into functional clusters"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Select
                  selectedOption={selectedView}
                  onChange={({ detail }) => setSelectedView(detail.selectedOption)}
                  options={[
                    { label: 'Overview', value: 'overview' },
                    { label: 'Detailed view', value: 'detailed' }
                  ]}
                />
                <ButtonDropdown
                  items={[
                    { text: 'Export classification', id: 'export' },
                    { text: 'Refresh analysis', id: 'refresh' },
                    { text: 'View relationships', id: 'relationships' }
                  ]}
                >
                  Actions
                </ButtonDropdown>
              </SpaceBetween>
            }
          >
            Class Clusters
          </Header>
        }
      >
        <ColumnLayout columns={2}>
          {/* Distribution Chart */}
          <Container
            header={
              <Header variant="h3">
                Cluster Distribution
              </Header>
            }
          >
            <PieChart
              data={data.summary.clusters}
              detailPopoverContent={(datum, sum) => [
                { key: 'Classes', value: datum.value },
                { key: 'Percentage', value: `${((datum.value / sum) * 100).toFixed(1)}%` }
              ]}
              size="large"
              variant="donut"
              innerMetricDescription="Total Classes"
              innerMetricValue={data.summary.totalClasses}
              onHighlightChange={({ detail }) => 
                setSelectedCluster(detail.highlightedSegment?.title)
              }
            />
          </Container>

          {/* Cluster Summary */}
          <Container
            header={
              <Header variant="h3">
                Cluster Metrics
              </Header>
            }
          >
            <Cards
              cardDefinition={{
                header: item => (
                  <Header variant="h4">{item.title}</Header>
                ),
                sections: [
                  {
                    id: 'metrics',
                    content: item => (
                      <ColumnLayout columns={2}>
                        <Box>
                          <SpaceBetween size="xs">
                            <Box variant="awsui-key-label">Classes</Box>
                            <Box variant="h3">{item.value}</Box>
                          </SpaceBetween>
                        </Box>
                        <Box>
                          <SpaceBetween size="xs">
                            <Box variant="awsui-key-label">Percentage</Box>
                            <Box variant="h3">
                              {((item.value / data.summary.totalClasses) * 100).toFixed(1)}%
                            </Box>
                          </SpaceBetween>
                        </Box>
                      </ColumnLayout>
                    )
                  }
                ]
              }}
              items={data.summary.clusters}
              cardsPerRow={[
                { cards: 2 },
                { minWidth: 500, cards: 2 }
              ]}
            />
          </Container>
        </ColumnLayout>
      </Container>

      {/* Cluster Details */}
      <Container
        header={
          <Header
            variant="h2"
            description="Detailed breakdown of classes within each cluster"
          >
            Cluster Details
          </Header>
        }
      >
        <SpaceBetween size="m">
          <TextFilter
            filteringText={filterText}
            onChange={({ detail }) => setFilterText(detail.filteringText)}
            placeholder="Find classes"
          />
          
            <Table
              columnDefinitions={tableColumns} // Use the tableColumns you defined earlier
              items={Object.values(data.details)
                .flatMap(cluster => cluster.classes)
                .filter(item => 
                  item.name.toLowerCase().includes(filterText.toLowerCase())
                )}
              sortingDisabled={false}
              variant="container"
              stickyHeader
              stripedRows
            />
        </SpaceBetween>
      </Container>

      {/* Class Details Modal */}
      <Modal
        visible={!!selectedClass}
        onDismiss={() => setSelectedClass(null)}
        header={selectedClass?.name}
        size="large"
      >
        {selectedClass && renderClassDetails(selectedClass)}
      </Modal>
    </SpaceBetween>
  );
};
